import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUser } from '../../context/UserContext';
import { api } from '../../services/api';
import FeedComposerCollapsed from './FeedComposerCollapsed';
import FeedComposerExpanded, { type Visibility } from './FeedComposerExpanded';
import styles from './FeedComposer.module.scss';

interface FeedComposerProps {
  onPostCreated?: () => void;
}

interface ComposerFormValues {
  body: string;
}

const MAX_LENGTH = 2000;

export default function FeedComposer({ onPostCreated }: FeedComposerProps) {
  const { user } = useUser();
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, watch, reset, formState } = useForm<ComposerFormValues>({
    defaultValues: { body: '' },
  });

  const body = watch('body');
  const { isSubmitting } = formState;
  const canSubmit = body.trim().length > 0 && !isSubmitting;

  // Clear error when user starts typing
  useEffect(() => {
    if (body && error) {
      setError(null);
    }
  }, [body]);

  if (!user) {
    return null;
  }

  const onSubmit = handleSubmit(async (data) => {
    const trimmedBody = data.body.trim();
    if (trimmedBody.length === 0) return;

    try {
      await api.social.createPost(trimmedBody, visibility);
      reset();
      setVisibility('public');
      setIsExpanded(false);
      setError(null);
      onPostCreated?.();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to create post';
      setError(message);
    }
  });

  return (
    <div className={styles.composer}>
      {isExpanded ? (
        <FeedComposerExpanded
          register={register}
          body={body}
          visibility={visibility}
          onVisibilityChange={setVisibility}
          onSubmit={onSubmit}
          onClickOutside={() => setIsExpanded(false)}
          isSubmitting={isSubmitting}
          canSubmit={canSubmit}
          error={error}
          maxLength={MAX_LENGTH}
        />
      ) : (
        <FeedComposerCollapsed
          avatarUrl={user.profileImageUrl || user.avatarUrl}
          username={user.username}
          onActivate={() => setIsExpanded(true)}
        />
      )}
    </div>
  );
}
